import { Dispatch, SetStateAction } from 'react';

import { useNavigation } from '@atom/hooks/useNavigation';
import { Icon, List } from '@atom/mui';
import colors from '@atom/styles/colors';
import { SearchResultsType } from '@atom/types/search';
import { isNilOrEmpty } from '@atom/utilities/validationUtilities';

import AssetResultItem from './AssetResultItem';
import UserResultItem from './UserResultItem';
import WorkResultItem from './WorkResultItem';

import classes from './searchBar.module.css';

const styles = {
  icon: {
    color: colors.brand.blue,
    marginRight: '0.5rem',
  },
  list: {
    padding: '0',
  },
};

interface Props {
  query: string;
  results: SearchResultsType;
  setFocused: Dispatch<SetStateAction<boolean>>;
}

const SearchResults = ({ query, results, setFocused }: Props) => {
  const [, { navigate }] = useNavigation();

  const showInventoryResults = !isNilOrEmpty(results?.assets);
  const showWorkResults = !isNilOrEmpty(results?.workOrders);
  const showUserResults = !isNilOrEmpty(results?.users);
  const hasResults = showInventoryResults || showWorkResults || showUserResults;

  const handleNavigate = () => {
    setFocused(false);
    navigate(`/search?query=${query}`);
  };

  return (
    <div>
      {!hasResults && (
        <div className={classes.emptySearch}>No matching search results</div>
      )}
      {hasResults && (
        <div className={classes.navigateContainer} onClick={handleNavigate}>
          <Icon style={styles.icon}>search</Icon>
          <span>See all results with </span>
          <span className={classes.queryDisplay}>{query}</span>
        </div>
      )}
      {showInventoryResults && (
        <>
          <div className={classes.sectionTitle}>Inventory</div>
          <List style={styles.list}>
            {results?.assets?.map(asset => (
              <AssetResultItem
                key={asset.id}
                query={query}
                result={asset}
                setFocused={setFocused}
              />
            ))}
          </List>
        </>
      )}
      {showWorkResults && (
        <>
          <div className={classes.sectionTitle}>Work</div>
          <List style={styles.list}>
            {results?.workOrders?.map(workOrder => (
              <WorkResultItem
                key={workOrder.id}
                query={query}
                result={workOrder}
                setFocused={setFocused}
              />
            ))}
          </List>
        </>
      )}
      {showUserResults && (
        <>
          <div className={classes.sectionTitle}>Team</div>
          <List style={styles.list}>
            {results?.users?.map(user => (
              <UserResultItem
                key={user.id}
                query={query}
                result={user}
                setFocused={setFocused}
              />
            ))}
          </List>
        </>
      )}
    </div>
  );
};

export default SearchResults;
